import { useState } from 'react';

/**
 * ImageCard Component
 *
 * A reusable component for displaying images with lazy loading,
 * aspect ratio control, and consistent styling.
 *
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for accessibility
 * @param {string} aspectRatio - Aspect ratio class (default: 'aspect-photo')
 * @param {string} className - Additional CSS classes
 * @param {boolean} rounded - Apply rounded corners (default: true)
 * @param {boolean} shadow - Apply shadow effect (default: true)
 */
const ImageCard = ({
  src,
  alt,
  aspectRatio = 'aspect-photo',
  className = '',
  rounded = true,
  shadow = true,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const baseClasses = `
    w-full
    ${aspectRatio}
    object-cover
    ${rounded ? 'rounded-lg' : ''}
    ${shadow ? 'shadow-card' : ''}
    transition-opacity duration-300
    ${isLoaded ? 'opacity-100' : 'opacity-0'}
  `.trim().replace(/\s+/g, ' ');

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  if (hasError) {
    return (
      <div
        className={`
          ${aspectRatio}
          ${rounded ? 'rounded-lg' : ''}
          flex items-center justify-center
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        style={{
          backgroundColor: 'var(--color-theme-2)',
          borderColor: 'var(--color-border-light)',
          borderWidth: '1px',
          borderStyle: 'solid'
        }}
      >
        <span style={{ color: 'var(--color-muted)' }} className="text-sm">
          Image unavailable
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading placeholder */}
      {!isLoaded && (
        <div
          className={`
            absolute inset-0
            ${aspectRatio}
            ${rounded ? 'rounded-lg' : ''}
            animate-pulse
          `.trim().replace(/\s+/g, ' ')}
          style={{ backgroundColor: 'var(--color-theme-3)' }}
        />
      )}

      {/* Actual image */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        className={`${baseClasses} ${className}`}
      />
    </div>
  );
};

export default ImageCard;
